import { useEffect, useRef } from 'react';
import { createFireworksAudio } from './fireworksAudio';

const COLORS = ['#1F6B3A', '#FFFEF8', '#CE1126', '#F4C430', '#FF3364'];

function reducedMotion() {
  return typeof window !== 'undefined'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function burst(particles, x, y, color) {
  const count = 72 + Math.floor(Math.random() * 36);
  for (let i = 0; i < count; i += 1) {
    const angle = (Math.PI * 2 * i) / count + Math.random() * 0.14;
    const speed = 2.4 + Math.random() * 5.6;
    particles.push({
      x,
      y,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed,
      life: 1,
      decay: 0.008 + Math.random() * 0.008,
      size: 2.1 + Math.random() * 2.8,
      color: Math.random() > 0.78 ? COLORS[Math.floor(Math.random() * COLORS.length)] : color
    });
  }
}

export default function useFireworks(canvasRef, active) {
  const rafRef = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !active || reducedMotion()) return undefined;

    const ctx = canvas.getContext('2d');
    if (!ctx) return undefined;

    const rockets = [];
    const particles = [];
    let lastSpawn = 0;
    let running = true;
    const started = performance.now();
    const audio = createFireworksAudio();

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.floor(window.innerWidth * dpr);
      canvas.height = Math.floor(window.innerHeight * dpr);
      canvas.style.width = `${window.innerWidth}px`;
      canvas.style.height = `${window.innerHeight}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener('resize', resize);

    const tick = (now) => {
      if (!running) return;
      const w = window.innerWidth;
      const h = window.innerHeight;
      ctx.clearRect(0, 0, w, h);
      ctx.globalCompositeOperation = 'lighter';

      const cadence = now - started < 3500 ? 160 : 320;
      if (now - lastSpawn > cadence + Math.random() * 140) {
        lastSpawn = now;
        const volley = Math.random() > 0.55 ? 2 : 1;
        for (let n = 0; n < volley; n += 1) {
          rockets.push({
            x: w * (0.1 + Math.random() * 0.8),
            y: h + 6,
            vx: (Math.random() - 0.5) * 1.35,
            vy: -(8.8 + Math.random() * 3.8),
            color: COLORS[Math.floor(Math.random() * COLORS.length)]
          });
          audio.lift(now);
        }
      }

      for (let i = rockets.length - 1; i >= 0; i -= 1) {
        const rocket = rockets[i];
        rocket.x += rocket.vx;
        rocket.y += rocket.vy;
        rocket.vy += 0.065;
        ctx.fillStyle = rocket.color;
        ctx.beginPath();
        ctx.arc(rocket.x, rocket.y, 2.1, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillRect(rocket.x - 0.6, rocket.y + 3, 1.2, 9);
        if (rocket.vy >= -1.1 || rocket.y < h * (0.16 + Math.random() * 0.18)) {
          burst(particles, rocket.x, rocket.y, rocket.color);
          audio.boom(now);
          rockets.splice(i, 1);
        }
      }

      for (let i = particles.length - 1; i >= 0; i -= 1) {
        const spark = particles[i];
        spark.x += spark.vx;
        spark.y += spark.vy;
        spark.vy += 0.035;
        spark.vx *= 0.992;
        spark.life -= spark.decay;
        if (spark.life <= 0) {
          particles.splice(i, 1);
          continue;
        }
        ctx.globalAlpha = Math.max(spark.life, 0);
        ctx.fillStyle = spark.color;
        ctx.beginPath();
        ctx.arc(spark.x, spark.y, spark.size, 0, Math.PI * 2);
        ctx.fill();
      }

      ctx.globalAlpha = 1;
      ctx.globalCompositeOperation = 'source-over';
      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);

    return () => {
      running = false;
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener('resize', resize);
      audio.dispose();
    };
  }, [canvasRef, active]);
}
